import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { CalendarEvent } from 'angular-calendar';
import { ActivitySignUpComponent } from 'app/activity-sign-up/activity-sign-up.component';
import { CalendarEventResponse, CalendarEventTypeEnum } from 'app/models/CalendarEvent';
import { TimeAgoPipe } from 'app/pipes/time-ago.pipe';
import { zonedTimeToUtc } from 'date-fns-tz';
import * as moment from 'moment';
import { BehaviorSubject, firstValueFrom, merge, Observable, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { LiveDataService } from '../live-data/live-data.service';
import {
	AttendanceRecord,
	Location,
	Pinnable,
	SchoolActivity,
	SchoolActivityInstance,
	SchoolActivityRow,
	SchoolActivityScheduleType,
	SchoolActivityState,
	SchoolActivityStatus,
	User,
} from '../models';
import { AppState } from '../ngrx/app-state/app-state';
import {
	AddSchoolActivityInstanceAction,
	ClearSelectedActivityInstanceAction,
	CreateSchoolActivityInstancesBulkAction,
	GetSchoolActivityInstancesForFlexPeriodAction,
	GetSchoolActivityInstancesSuccessAction,
	RemoveSchoolActivityInstanceAction,
	RemoveSchoolActivityInstanceSuccessAction,
	SelectActivityInstanceAction,
	UpdateSchoolActivityInstanceSuccessAction,
} from '../ngrx/school-activities-instances/actions/school-activities-instances.actions';
import {
	getActivityInstancesCollection,
	getPastSchoolActivityInstances,
	getSchoolActivityInstanceById,
	getSchoolActivityInstancesForDate,
	getSelectedActivityInstance,
	getUpcomingSchoolActivityInstances,
} from '../ngrx/school-activities-instances/states/school-activities-instances-getters.state';
import {
	AddSchoolActivityAction,
	AddSchoolActivitySuccessAction,
	DeleteSchoolActivityAction,
	DeleteSchoolActivitySuccessAction,
	GetSchoolActivitiesAction,
	GetSchoolActivitiesByStatusAction,
	GetSchoolActivityByIdAction,
	UpdateSchoolActivityAction,
	UpdateSchoolActivitySuccessAction,
} from '../ngrx/school-activities/actions/school-activities.actions';
import { getSchoolActivityById, getSchoolActivityByInstanceId } from '../ngrx/school-activities/states/school-activities-getters.state';
import {
	BulkRemoveAttendeeAndSignUpAction,
	BulkSignOutForActivityAction,
	BulkSignUpForActivityAction,
	BulkSignUpForActivitySuccessAction,
	GetAttendeesForInstanceAction,
	RemoveAttendeeAndSignUpAction,
	RemoveAttendeeAndSignUpActionSuccess,
	SignUpForActivityAction,
} from '../ngrx/school-activity-attendees/actions/school-activity-attendees.actions';
import { getAttendeeByUserId, getAttendeesForInstances } from '../ngrx/school-activity-attendees/states/school-activity-attendees-getters.state';
import { AttendanceRecordService } from './attendance-record-service';
import { FeatureFlagService, FLAGS } from './feature-flag.service';
import { FlexPeriod, FlexVisibility } from './flex-period.service';
import { HttpService } from './http-service';
import { LiveUpdateEvent, LiveUpdateService } from './live-update.service';

export type ToBeDeletedCountResponse = {
	count: number;
};
export type ToBeDeletedCountReq = {
	activity_id: number;
	activity_data: CreateSchoolActivityReq;
};

export interface CreateSchoolActivityReq {
	name: string;
	icon: string;
	description: string;
	location_id: number;
	max_attendees: number;
	public_event: boolean;
	state: SchoolActivityState;
	status: SchoolActivityStatus;
	flex_period_id?: number;

	// Flex 2.0 fields - optional until we update the schedule-pass component
	schedule_type: SchoolActivityScheduleType;
	manager_ids?: number[];
	repeat_end_date?: string | null;
	custom_start_time?: string | null;
	custom_end_time?: string | null;
	custom_repeat_rule?: string | null;
	visibility?: FlexVisibility;
	visibility_student_ids?: number[];
	visibility_group_ids?: number[];

	selected_instance_times?: WireInstanceTimes[];
}

export interface CreateSchoolActivityInstanceReq {
	start: Date;
	end: Date;
	activityId: number;
	selected: boolean;
}

export interface GetAttendeesForInstanceReq {
	activity_instance_id: number;
	include_user_profiles: boolean;
	start_time?: string;
}

export interface SignUpForActivityReq {
	user_id: number;
	activity_id: number;
	scheduled_date: Date;
	activity_instance_id: number;
}

export interface BulkSignUpForActivityReq {
	user_ids: number[];
	activity_id: number;
	activity_instance_id: number;
	scheduled_date?: Date;
	override_limit?: boolean;
}

export interface WireInstanceTimes {
	start_time: string; // ISO string
	end_time: string; // ISO string
}

export function ToWireInstanceTimes(instanceTimes: InstanceTimes[]): WireInstanceTimes[] {
	return instanceTimes.map((i) => ({ start_time: i.start.toISOString(), end_time: i.end.toISOString() }));
}

export interface UpdateSchoolActivityRequest extends CreateSchoolActivityReq {
	id: number;
	update_visibility_students_groups?: boolean;
	selected_instance_times?: WireInstanceTimes[];
}

export interface SchoolActivityInstancesReq {
	from?: Date;
	to?: Date;
	activity: SchoolActivity;
	timezone: string;
	flexPeriod?: FlexPeriod | undefined;
}

export interface SchoolActivityInstancesForPeriodReq {
	day: Date;
	activities: SchoolActivity[];
	flexPeriod: FlexPeriod;
	timezone: string;
}

export interface SchoolActivityAttendee {
	id: number;
	activity_id: number;
	activity_instance_id: number;
	activity_name: string;
	flex_period_id: number;
	user_id: number;
	pass_id: number;
	created_at: string;
	updated_at: string;
	state: string;
	school_id: number;
	assigner_id: number;
	start_time?: Date;
	user?: User;
}

export interface AttendeeData {
	school_activity_id: number;
	school_activity_instance_id: number;
	start_time: string;
	end_time: string;
	user_ids: number[];
	assigner_id?: number;
	manager_ids?: number[];
	attendees: SchoolActivityAttendee[];
}

export type GetActivityInstancesReq = {
	start_time?: Date;
	end_time?: Date;
	activity_id?: number;
};

export type MaxCapacityValues = {
	errorMessage: string;
	maxCapacityString: string;
	maxCapacityNumber?: number;
};

export interface InstanceTimes {
	start: Date;
	end: Date;
}

export type SchoolActivityFilter = {
	status?: SchoolActivityStatus;
	flex_period_id?: number;
	ids?: number[];
	public_event?: boolean;
	limit?: number;
	offset?: number;
	search?: string;
	manager_id?: number;
};

export type ExportActivitiesReq = {
	flex_period_id?: number;
	date: string; // iso string
};
export type ExportActivitiesByUserIdReq = {
	user_id: number;
	start_time: Date;
	end_time: Date;
};

export type GetActivitiesByUserIdReq = {
	user_id: number;
	status: string;
	start_date: Date;
	end_date?: Date;
	offset?: number;
	limit?: number;
};

export type ActivityByUserId = {
	id: number; // activity attendee ID
	state: string;
	name: string;
	location_id: number;
	activity_id: number;
	flex_period_id: number;
	start_time: string;
	end_time: string;
	activity_instance_id: number;
	room_name: string;
	manager_names: string[];
	manager_ids: number[];
	attendance_record: AttendanceRecord | null;
};

export const DAYS_SHOW_ACTIVITY_FUTURE_TEACHER = 120;

@Injectable({
	providedIn: 'root',
})
export class SchoolActivityService {
	hasSearchResults$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	selectedStudents$: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
	attendeeIdsToDelete$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
	refreshingInstances$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	attendees$ = (instanceId: number): Observable<SchoolActivityAttendee[]> =>
		this.store
			.select((state) => getAttendeesForInstances(instanceId)(state))
			.pipe(
				map((at) => {
					return at.sort((a, b) => {
						if (a.user?.display_name && b.user?.display_name) {
							return a.user.display_name.localeCompare(b.user.display_name);
						}
						return 0;
					});
				})
			);

	activities$ = this.store.select('schoolActivities');
	activity$ = (id: number) => this.store.pipe(select(getSchoolActivityById(id)));
	instance$ = (id: number) => this.store.pipe(select(getSchoolActivityInstanceById(id)));
	activityForInstance$ = (instanceId: number) => this.store.pipe(select(getSchoolActivityByInstanceId(instanceId)));

	pastInstances$ = (activityId: number, currentDate: moment.Moment) =>
		this.store.pipe(
			select(getPastSchoolActivityInstances(activityId, currentDate)),
			map((instances) => {
				return instances?.sort((a, b) => new Date(b!.start_time).getTime() - new Date(a!.start_time).getTime()) || [];
			}),
			distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
		);

	upcomingInstances$ = (activityId: number, currentDate: moment.Moment) =>
		this.store.pipe(
			select(getUpcomingSchoolActivityInstances(activityId, currentDate)),
			map((instances) => {
				return instances?.sort((a, b) => new Date(a!.start_time).getTime() - new Date(b!.start_time).getTime()) || [];
			}),
			distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
		);

	instancesForDate$ = (date: Date) =>
		this.store.pipe(
			select(getSchoolActivityInstancesForDate(date)),
			map((instances) => {
				return instances?.sort((a, b) => new Date(a!.start_time).getTime() - new Date(b!.start_time).getTime()) || [];
			}),
			distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
		);

	attendeeByUserId$ = (userId: number) => this.store.pipe(select(getAttendeeByUserId(userId)));

	instances$: Observable<SchoolActivityInstance[]> = this.store.pipe(
		select(getActivityInstancesCollection),
		distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
	);

	instancesLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	selectedInstance$ = this.store.pipe(select(getSelectedActivityInstance));
	instanceTimes$: BehaviorSubject<InstanceTimes[]> = new BehaviorSubject<InstanceTimes[]>([]);

	constructor(
		private http: HttpService,
		private store: Store<AppState>,
		private featureFlagService: FeatureFlagService,
		private liveUpdateService: LiveUpdateService,
		private liveData: LiveDataService,
		private timeAgoPipe: TimeAgoPipe,
		private dialog: MatDialog,
		private attendanceRecordService: AttendanceRecordService
	) {}

	CreateActivity(createReq: CreateSchoolActivityReq): void {
		this.store.dispatch(AddSchoolActivityAction({ createReq: createReq }));
	}

	AddActivityToStore(activity: SchoolActivity): void {
		this.store.dispatch(AddSchoolActivitySuccessAction({ schoolActivity: activity }));
	}

	CreateActivityHTTP(body: CreateSchoolActivityReq): Observable<SchoolActivity> {
		return this.http.post<SchoolActivity>('v2/school_activities/add', body, undefined, false);
	}

	DeleteActivity(id: number): void {
		this.store.dispatch(DeleteSchoolActivityAction({ activityId: id }));
	}

	DeleteActivityHTTP(id: number): Observable<Record<string, never>> {
		return this.http.post<Record<string, never>>('v2/school_activities/delete', { id: id }, undefined, false);
	}

	RemoveAttendeeFromActivityInstanceAndSignUp(attendeeId: number, req?: SignUpForActivityReq, instance?: SchoolActivityInstance): void {
		this.store.dispatch(RemoveAttendeeAndSignUpAction({ attendeeId, req, instance }));
	}

	SignUpSelectedStudents(
		instance: SchoolActivityInstance,
		opts: { override: boolean; checkIn: boolean } = { override: false, checkIn: false }
	): void {
		const students = this.selectedStudents$.getValue();
		const userIds = students.map((user) => user.id);

		if (userIds.length > 0) {
			const req: BulkSignUpForActivityReq = {
				user_ids: userIds,
				activity_id: instance.activity_id,
				scheduled_date: new Date(instance.start_time),
				activity_instance_id: instance.id,
				override_limit: opts.override,
			};
			const attendeeIds = this.attendeeIdsToDelete$.getValue();
			this.BulkRemoveAttendeeFromActivityInstanceAndSignUp(attendeeIds, req, instance, opts.checkIn);
		}

		this.selectedStudents$.next([]);
	}

	BulkRemoveAttendeeFromActivityInstanceAndSignUp(
		attendeeIds: number[],
		req: BulkSignUpForActivityReq,
		instance: SchoolActivityInstance,
		checkIn = false
	): void {
		this.store.dispatch(BulkRemoveAttendeeAndSignUpAction({ attendeeIds, req, instance, checkIn }));
	}

	RemoveAttendeeFromActivityInstanceHTTP(antendeeRecordId: number): Observable<null> {
		return this.http.post<null>('v2/school_activities/attendee/delete', { id: antendeeRecordId }, undefined, false);
	}

	GetActivities(flexPeriodId?: number): void {
		this.store.dispatch(GetSchoolActivitiesAction({ flex_period_id: flexPeriodId }));
	}

	GetActivitiesHTTP(filterOptions: SchoolActivityFilter = {}): Observable<SchoolActivity[]> {
		return this.http.post<SchoolActivity[]>('v2/school_activities/list', filterOptions, undefined, false);
	}

	GetActivitiesByStatusHTTP(req: SchoolActivityFilter): Observable<SchoolActivity[]> {
		return this.http.post<SchoolActivity[]>('v2/school_activities/list', req, undefined, false);
	}

	GetActivitiesByStatus(req: SchoolActivityFilter, setAll: boolean): void {
		this.store.dispatch(
			GetSchoolActivitiesByStatusAction({
				req: req,
				setAll: setAll,
			})
		);
	}

	GetActivityById(schoolActivityId: number, includeVisibilites = false): void {
		this.store.dispatch(
			GetSchoolActivityByIdAction({
				activityId: schoolActivityId,
				includeVisibilites: includeVisibilites,
			})
		);
	}

	GetActivityByIdHTTP(schoolActivityId: number, includeVisibilites = false): Observable<SchoolActivity> {
		return this.http.post<SchoolActivity>(
			'v2/school_activities/get_by_id',
			{
				school_activity_id: schoolActivityId,
				include_visibilities: includeVisibilites,
			},
			undefined,
			false
		);
	}

	StudentSignUpForActivity(req: SignUpForActivityReq, instance: SchoolActivityInstance): void {
		this.store.dispatch(SignUpForActivityAction({ req, instance }));
	}

	StudentSignUpForActivityHTTP(req: SignUpForActivityReq): Observable<SchoolActivityAttendee> {
		return this.http.post<SchoolActivityAttendee>('v2/school_activities/attendee/add', req, undefined, false);
	}

	TeacherBulkSignUpForActivity(req: BulkSignUpForActivityReq, instance: SchoolActivityInstance): void {
		this.store.dispatch(BulkSignUpForActivityAction({ req, instance, checkIn: false }));
	}

	TeacherBulkSignUpForActivityHTTP(req: BulkSignUpForActivityReq, instance: SchoolActivityInstance): Observable<SchoolActivityAttendee[]> {
		return this.http.post<SchoolActivityAttendee[]>('v2/school_activities/attendee/bulk_add', req, undefined, false);
	}

	TeacherBulkSignOutForActivity(attendeeIds: number[], instance: SchoolActivityInstance): void {
		this.store.dispatch(BulkSignOutForActivityAction({ attendeeIds: attendeeIds }));
		if (instance.current_num_attendees !== undefined) {
			instance.current_num_attendees = instance.current_num_attendees - attendeeIds.length;
		} else if (instance.current_num_attendees === undefined) {
			instance.current_num_attendees = 0;
		}
		this.UpdateActivityInstance(instance, instance.id, instance.id);
	}

	TeacherBulkSignOutForActivityHTTP(attendeeIds: number[]): Observable<SchoolActivityAttendee[]> {
		return this.http.post<SchoolActivityAttendee[]>('v2/school_activities/attendee/bulk_delete', { attendee_ids: attendeeIds }, undefined, false);
	}

	UpdateActivityInstance(instance: SchoolActivityInstance, oldId: number, newId: number): void {
		// todo this needs to be tested from all uses of it
		this.DeleteActivityInstanceFromStore(oldId);
		instance.id = newId;
		this.store.dispatch(AddSchoolActivityInstanceAction({ instance }));
		this.SelectActivityInstance(newId);
	}

	CreateActivityInstanceBulk(instanceReqs: CreateSchoolActivityInstanceReq[]): void {
		this.store.dispatch(CreateSchoolActivityInstancesBulkAction({ instanceReqs }));
	}

	CreateActivityInstanceBulkHTTP(instanceTimes: InstanceTimes[], activityId: number): Observable<SchoolActivityInstance[]> {
		const instanceTimesIso = ToWireInstanceTimes(instanceTimes);
		return this.http.post<SchoolActivityInstance[]>(
			'v2/school_activities/instances/bulk_add',
			{ instance_times: instanceTimesIso, activity_id: activityId },
			undefined,
			false
		);
	}

	UpdateActivityHTTP(activity: Partial<UpdateSchoolActivityRequest>): Observable<SchoolActivity> {
		return this.http.post<SchoolActivity>('v2/school_activities/update', activity, undefined, false);
	}

	GetToBeDeletedCountHTTP(req: ToBeDeletedCountReq): Observable<number> {
		return this.http
			.post<ToBeDeletedCountResponse>('v2/school_activities/get_to_be_deleted_count', req, undefined, false)
			.pipe(map((resp) => resp.count));
	}

	UpdateActivity(activityReq: Partial<UpdateSchoolActivityRequest>, activity: SchoolActivity | undefined = undefined): void {
		// If we omit `public_event`, the backend interprets it as `false` and hides
		// activities when updating. Set it explicitly if not present in the partial
		// update object.
		if (activityReq.public_event == undefined && activity != undefined) {
			activityReq.public_event = activity.public_event;
		}
		if (this.featureFlagService.isFeatureEnabledV2(FLAGS.ActivityManagement)) {
			// When archiving, set the repeat end date to today
			if (activityReq.status == 'archived') {
				activityReq.repeat_end_date = moment().toISOString();
			}
		}
		activityReq.update_visibility_students_groups = false;
		if (
			(this.featureFlagService.isFeatureEnabledV2(FLAGS.ActivityManagement) && activityReq?.visibility_student_ids) ||
			activityReq?.visibility_group_ids
		) {
			activityReq.update_visibility_students_groups = true;
		}
		this.store.dispatch(
			UpdateSchoolActivityAction({
				req: activityReq,
			})
		);
	}

	UpdateActivityInstanceHTTP(instance: Partial<SchoolActivityInstance>): Observable<SchoolActivityInstance> {
		return this.http.post<SchoolActivityInstance>('v2/school_activities/instances/update', instance, undefined, false);
	}

	DeleteActivityInstance(id: number): void {
		this.store.dispatch(RemoveSchoolActivityInstanceAction({ instanceId: id }));
	}

	async DeleteActivityInstanceBulk(activityId: number, instanceIds: number[]): Promise<void> {
		if (instanceIds.length === 0) {
			return;
		}

		await this.DeleteActivityInstanceBulkHTTP(activityId, instanceIds);
		for (const id of instanceIds) {
			this.store.dispatch(RemoveSchoolActivityInstanceSuccessAction({ instanceId: id }));
		}
	}

	async DeleteActivityInstanceBulkHTTP(activityId: number, instanceIds: number[]): Promise<void> {
		return await firstValueFrom(
			this.http.post<void>(
				'v2/school_activities/instances/bulk_delete',
				{
					activity_id: activityId,
					instance_ids: instanceIds,
				},
				undefined,
				false
			)
		);
	}

	DeleteActivityInstanceFromStore(id: number): void {
		this.store.dispatch(RemoveSchoolActivityInstanceSuccessAction({ instanceId: id }));
	}

	DeleteActivityInstanceHTTP(id: number): Observable<Record<string, never>> {
		return this.http.post<Record<string, never>>('v2/school_activities/instances/delete', { id: id }, undefined, false);
	}

	GetAttendeeRecordForStudent(activity_instance_id: number, studentId: number, start_time: Date): Observable<SchoolActivityAttendee | null> {
		return this.http
			.post<SchoolActivityAttendee[]>(
				'v2/school_activities/attendee/list',
				{ activity_instance_id: activity_instance_id, user_id: studentId, start_time: start_time.toISOString() },
				undefined,
				false
			)
			.pipe(map((l) => (l.length > 0 ? l[0] : null)));
	}

	GetAttendeeRecordsForTimePeriod(from: Date, to: Date): Observable<SchoolActivityAttendee[]> {
		return this.http.post<SchoolActivityAttendee[]>(
			'v2/school_activities/attendee/list',
			{ start_time: from.toISOString(), end_time: to.toISOString() },
			undefined,
			false
		);
	}

	async LoadActivityInstance(id: number) {
		let instance = await firstValueFrom(this.instance$(id));
		if (!instance) {
			instance = await firstValueFrom(this.GetActivityInstanceByIdHTTP(id));
			this.store.dispatch(GetSchoolActivityInstancesSuccessAction({ instances: [instance] }));
		}

		if ((await firstValueFrom(this.activity$(instance.activity_id))) == undefined) {
			this.GetActivityById(instance.activity_id);
		}
	}

	GetActivityInstanceByIdHTTP(id: number): Observable<SchoolActivityInstance> {
		return this.http.post<SchoolActivityInstance>('v2/school_activities/instances/get_by_id', { id: id }, undefined, false);
	}

	GetActivityInstancesHTTP(req: GetActivityInstancesReq): Observable<SchoolActivityInstance[]> {
		return this.http.post<SchoolActivityInstance[]>('v2/school_activities/instances/list', req, undefined, false);
	}

	GetStartedActivityInstances(locationId: number, bufferMinutes: number): Observable<SchoolActivityInstance[]> {
		return this.http.post<SchoolActivityInstance[]>(
			'v2/school_activities/instances/location/list',
			{ location_id: locationId, buffer: bufferMinutes, include_past_instances: false },
			undefined,
			false
		);
	}

	GetActivitiesByUserId(req: GetActivitiesByUserIdReq): Observable<ActivityByUserId[]> {
		return this.http.post<ActivityByUserId[]>('v2/school_activities/attendee/get_by_user_id', req, undefined, false).pipe(
			map((resp) => {
				const activities = resp.map((activity) => {
					const attendance = AttendanceRecord.fromJSON(activity.attendance_record);
					activity.attendance_record = attendance;

					return activity;
				});

				// Dispatch state update for instance records
				const instances: SchoolActivityInstance[] = activities.map((a) => {
					return {
						id: a.activity_instance_id,
						activity_id: a.activity_id,
						start_time: a.start_time,
						end_time: a.end_time,
					};
				});
				this.store.dispatch(GetSchoolActivityInstancesSuccessAction({ instances }));

				// Dispatch state update for attendance records
				const attendance = activities.map((a) => a.attendance_record).filter((a) => a != null) as AttendanceRecord[];
				this.attendanceRecordService.insertRecords(attendance);

				return activities;
			})
		);
	}

	async GetAttendeesForInstance(req: GetAttendeesForInstanceReq): Promise<SchoolActivityAttendee[]> {
		const resp = this.http.post<SchoolActivityAttendee[]>('v2/school_activities/attendee/list', req, undefined, false);

		const attendees = await firstValueFrom(resp);
		this.store.dispatch(GetAttendeesForInstanceAction({ attendees }));

		return attendees;
	}

	/**
	 * returns a beginning date object and an end date object for a specified date and timezone
	 * @param date
	 * @private
	 */
	getBeginningAndEndDates(date: Date, timezone: string): { start: Date; end: Date } {
		// JavaScript treat its "Date" type as a string as well, so we need to be sure
		if (!(date instanceof Date)) {
			date = new Date(date);
		}

		date = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
		return {
			start: moment(date).startOf('day').toDate(),
			end: moment(date).endOf('day').toDate(),
		};
	}

	GetActivityInstancesByPeriod(data: SchoolActivityInstancesForPeriodReq): void {
		this.store.dispatch(GetSchoolActivityInstancesForFlexPeriodAction({ req: data }));
	}

	GetActivityInstancesByPeriodHTTP(data: SchoolActivityInstancesForPeriodReq): Observable<SchoolActivityInstance[]> {
		const school = this.http.getSchool();

		if (!school) {
			return throwError(() => 'No school available');
		}

		const { start, end } = this.getBeginningAndEndDates(data.day, school.timezone);

		return this.http
			.post<SchoolActivityInstance[]>(
				'v2/school_activities/instances/list',
				{ start_time: start.toISOString(), end_time: end.toISOString() },
				undefined,
				false
			)
			.pipe(
				map((instances) => {
					this.instancesLoaded$.next(true);
					return instances;
				})
			);
	}

	async GetActivityInstancesById(data: SchoolActivityInstancesReq): Promise<SchoolActivityInstance[]> {
		const instancesObservable = this.GetActivityInstancesByIdHTTP(data);

		const instances = await firstValueFrom(instancesObservable);
		this.store.dispatch(GetSchoolActivityInstancesSuccessAction({ instances }));

		return instances;
	}

	GetActivityInstancesByIdHTTP(data: SchoolActivityInstancesReq): Observable<SchoolActivityInstance[]> {
		// update the date parsing logic here too
		return this.http
			.post<SchoolActivityInstance[]>(
				'v2/school_activities/instances/list',
				{ start_time: data.from?.toISOString(), end_time: data.to?.toISOString(), activity_id: data.activity?.id },
				undefined,
				false
			)
			.pipe(
				map((instances) => {
					// Sort instances by start_time
					instances.sort((a, b) => {
						const startTimeA = new Date(a.start_time).getTime();
						const startTimeB = new Date(b.start_time).getTime();
						return startTimeA - startTimeB;
					});
					return instances;
				})
			);
	}

	private utcDateTimeZone(time: Date, zone: string): Date {
		if (zone) {
			return zonedTimeToUtc(time, zone);
		}
		return time;
	}

	handleMaxCapacity(isFocused: boolean, fieldValue: string | number | undefined): MaxCapacityValues {
		const numberValue = fieldValue ? parseInt(fieldValue.toString()) : null;
		if (isFocused) {
			const values: MaxCapacityValues = { errorMessage: '', maxCapacityString: '' };
			if (numberValue) {
				values.maxCapacityNumber = numberValue;
			}
			return values;
		} else {
			if (fieldValue === '0' || fieldValue === 0) {
				return { errorMessage: 'Please add a max capacity greater than “0”.', maxCapacityString: '' };
			}
			if (fieldValue === undefined || fieldValue === '') {
				return { errorMessage: 'Please add a max capacity for this activity.', maxCapacityString: '' };
			}
			return {
				errorMessage: '',
				maxCapacityString: `${numberValue} student${numberValue !== 1 ? 's' : ''}`,
			};
		}
	}

	parseSchoolActivity(activity: SchoolActivity, locs: Location[], pins: Pinnable[], flexPeriods: FlexPeriod[]): SchoolActivityRow {
		const loc = locs.find((location) => location.id === activity.location_id);
		const pin = pins.find((p) => (loc?.category && p?.category == loc.category) || p?.location?.id === loc?.id);
		activity.color_profile = pin?.color_profile;
		const flexPeriodName = flexPeriods.find((flexPeriod) => flexPeriod.id === activity.flex_period_id)?.name;
		return {
			...activity,
			location_icon: pin?.icon || '',
			location_name: loc?.title || '',
			location: loc ? loc : undefined,
			flex_period_name: flexPeriodName || '',
		};
	}

	getTeacherNames(activity: Partial<SchoolActivity>): string {
		if (activity.managers && activity.managers.length > 0) {
			return activity.managers.map((m: User) => m.display_name).join(', ');
		}
		// NOTE: teacher_name is a fallback to allow legacy activities to show correct titles
		if (activity.teacher_name) {
			return activity.teacher_name;
		}
		return '';
	}

	private createActivityPipeForTeacher(userId: number) {
		return (source: Observable<any>) =>
			source.pipe(
				filter((pe) => !!pe),
				filter((event) => {
					const activity = event.data;
					if (Array.isArray(activity)) {
						return (
							activity.filter((p) => {
								const managerIds = p.managers.map((m: User) => m.id);
								return p.user_id === userId || managerIds.includes(userId);
							}).length > 0
						);
					} else {
						const managerIds = activity.managers.map((m: User) => m.id);
						return activity.user_id === userId || managerIds.includes(userId);
					}
				}),
				catchError((e, originalObs) => {
					console.log('Error in createActivityPipeForTeacher', e);
					return originalObs;
				})
			);
	}

	watchActivityEventsForTeacher(userId: number): Observable<any> {
		const activityPipe = this.createActivityPipeForTeacher(userId);
		return this.liveUpdateService.isConnected$.pipe(
			filter(Boolean),
			distinctUntilChanged(),
			switchMap(() => {
				return merge(
					this.liveUpdateService.listen('school_activity.create').pipe(activityPipe),
					this.liveUpdateService.listen('school_activity.update').pipe(activityPipe),
					this.liveUpdateService.listen('school_activity.delete').pipe(activityPipe)
				);
			}),
			map((event) => {
				if (!event) {
					return;
				}
				if (Array.isArray(event)) {
					for (const item of event) {
						this.handleEvent(item.action, item.data);
					}
				} else {
					this.handleEvent(event.action, event.data);
				}
			}),
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	watchActivityInstanceEventsForTeacher(): Observable<any> {
		return this.liveUpdateService.isConnected$.pipe(
			filter(Boolean),
			distinctUntilChanged(),
			switchMap(() => {
				return merge(
					this.liveUpdateService.listen('school_activity_instances.create'),
					this.liveUpdateService.listen('school_activity_instances.update'),
					this.liveUpdateService.listen('school_activity_instances.delete')
				);
			}),
			tap((event) => {
				let activityId = 0;
				if (event.data) {
					const inst = Array.isArray(event.data) ? event.data[0] : event.data;
					if (this.isSchoolActivityInstance(inst)) {
						activityId = inst.activity_id;
					}
				}

				void firstValueFrom(this.store.select(getSchoolActivityById(activityId))).then((activity) => {
					if (!activity) {
						return;
					}

					if (Array.isArray(event)) {
						for (const item of event as LiveUpdateEvent[]) {
							this.handleEvent(item.action, item.data);
						}
					} else {
						this.handleEvent(event.action, event.data);
					}
				});
			}),
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	watchActivityAttendeeEventsForTeacher(): Observable<any> {
		return this.liveUpdateService.isConnected$.pipe(
			filter(Boolean),
			distinctUntilChanged(),
			switchMap(() => {
				return merge(
					this.liveUpdateService.listen('school_activity_attendees.create'),
					this.liveUpdateService.listen('school_activity_attendees.update'),
					this.liveUpdateService.listen('school_activity_attendees.delete')
				);
			}),
			map(async (event) => {
				let instanceId = 0;

				if (event.data && Array.isArray(event.data)) {
					if (this.isSchoolActivityAttendee(event.data[0])) {
						instanceId = event.data[0].activity_instance_id;
					}
					if (this.isSchoolActivityBulkAttendee(event.data[0])) {
						instanceId = event.data[0].school_activity_instance_id;
					}
				} else {
					if (this.isSchoolActivityAttendee(event.data)) {
						instanceId = event.data.activity_instance_id;
					}
				}

				if (instanceId === 0) {
					return;
				}
				const instance = await firstValueFrom(this.store.select(getSchoolActivityInstanceById(instanceId)));
				if (!instance) {
					return;
				}

				if (Array.isArray(event)) {
					for (const item of event) {
						this.handleEvent(item.action, item.data);
					}
				} else {
					this.handleEvent(event.action, event.data);
				}
			}),
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	private isSchoolActivityInstance(data: unknown): data is SchoolActivityInstance {
		return (data as SchoolActivityInstance).activity_id !== undefined;
	}

	private isSchoolActivityAttendee(data: unknown): data is SchoolActivityAttendee {
		return (data as SchoolActivityAttendee).activity_instance_id !== undefined;
	}

	private isSchoolActivityBulkAttendee(data: unknown): data is AttendeeData {
		return (data as AttendeeData).school_activity_instance_id !== undefined;
	}

	private handleEvent(action: string, data: any) {
		switch (action) {
			case 'school_activity.create':
				this.store.dispatch(AddSchoolActivitySuccessAction({ schoolActivity: data[0] }));
				break;
			case 'school_activity.update':
				this.store.dispatch(UpdateSchoolActivitySuccessAction({ schoolActivity: data, fromLiveUpdate: true }));
				break;
			case 'school_activity.delete':
				this.store.dispatch(DeleteSchoolActivitySuccessAction({ activityId: data.id }));
				break;
			case 'school_activity_instances.create':
			case 'school_activity_instances.update':
				this.store.dispatch(UpdateSchoolActivityInstanceSuccessAction({ instance: data }));
				break;
			case 'school_activity_instances.delete':
				this.store.dispatch(RemoveSchoolActivityInstanceSuccessAction({ instanceId: data.id }));
				break;
			case 'school_activity_attendees.create':
				if (this.isSchoolActivityBulkAttendee(data[0])) {
					this.store.dispatch(BulkSignUpForActivitySuccessAction({ attendees: data[0].attendees }));

					// Refresh instance attendance when receiving new attendees
					void firstValueFrom(this.attendanceRecordService.getRecordsForInstanceId(data[0].school_activity_instance_id));
				}
				break;
			case 'school_activity_attendees.delete':
				if (this.isSchoolActivityAttendee(data)) {
					this.store.dispatch(RemoveAttendeeAndSignUpActionSuccess({ attendeeId: data.id }));
				}
				break;
		}
	}

	WatchActivityEventsForStudent(): Observable<any> {
		return this.liveUpdateService.isConnected$.pipe(
			filter(Boolean),
			distinctUntilChanged(),
			switchMap(() => {
				return merge(
					this.liveUpdateService.listen('school_activity.create'),
					this.liveUpdateService.listen('school_activity.update'),
					this.liveUpdateService.listen('school_activity.delete'),
					this.liveUpdateService.listen('school_activity_instances.create'),
					this.liveUpdateService.listen('school_activity_instances.update'),
					this.liveUpdateService.listen('school_activity_instances.delete')
				);
			}),
			map((event) => {
				if (!event) {
					return;
				}
				if (Array.isArray(event)) {
					for (const item of event) {
						this.handleEvent(item.action, item.data);
					}
				} else {
					this.handleEvent(event.action, event.data);
				}
			}),
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	listenForActivityAttendeeUpdate(): Observable<LiveUpdateEvent> {
		return this.liveData.watchUpdatedActivityAttendee();
	}

	ClearSelectedActivityInstance(): void {
		this.store.dispatch(ClearSelectedActivityInstanceAction());
	}

	SelectActivityInstance(instanceId: number): void {
		this.store.dispatch(SelectActivityInstanceAction({ instanceId: instanceId }));
	}

	// This determines if the user is an attendee of a school activity sent via websocket event.
	// The event data is of type AttendeeData or SchoolActivityAttendee.
	// AttendeeData will have an array of user_ids, while SchoolActivityAttendee will have a single user_id.
	userIsAttendee(userId: number, event: LiveUpdateEvent | null): boolean {
		return (
			!!event?.data &&
			Array.isArray(event.data) &&
			event.data.length > 0 &&
			(('user_ids' in event.data[0] && event.data[0].user_ids.includes(userId)) || ('user_id' in event.data[0] && event.data[0].user_id === userId))
		);
	}

	exportInstanceByFlex(flexPeriodId: number, date: Date): Observable<string> {
		const body: ExportActivitiesReq = {
			flex_period_id: flexPeriodId,
			date: date.toISOString(),
		};
		return this.http.post<string>('v2/school_activities/instances/export', body, { responseType: 'text' as 'json' }, false);
	}

	exportInstanceForCustom(date: Date): Observable<string> {
		const body: ExportActivitiesReq = {
			date: date.toISOString(),
		};
		return this.http.post<string>('v2/school_activities/instances/export', body, { responseType: 'text' as 'json' }, false);
	}

	exportAttendeesByFlex(flexPeriodId: number, date: Date, attending = true): Observable<string> {
		const body = {
			flex_period_id: flexPeriodId,
			date: date.toISOString(),
		};
		let route = 'v2/school_activities/attendee/export';
		if (!attending) {
			route += '/absentees';
		}
		return this.http.post<string>(route, body, { responseType: 'text' as 'json' }, false);
	}

	exportActivitiesByUserId(req: ExportActivitiesByUserIdReq): Observable<string> {
		return this.http.post<string>('v2/school_activities/instances/export_by_user_id', req, { responseType: 'text' as 'json' }, false);
	}

	GetInstanceCountByFlexIdHTTP(flexPeriodId: number): Observable<number> {
		const body: { id: number } = { id: flexPeriodId };
		return this.http
			.post<{ count: number }>('v2/school_activities/instances/get_instance_count_by_flex_id', body, undefined, false)
			.pipe(map((resp) => resp.count));
	}

	openActivitySignUpDialog(calendarEvent: CalendarEvent): Promise<SchoolActivityInstance | undefined> {
		const end = calendarEvent.end ?? new Date();
		if (this.timeAgoPipe.transform(end) > 0) {
			return Promise.resolve(undefined);
		}

		let selectedActivityId: number | undefined;
		const flexPeriodId = calendarEvent.meta?.flexPeriodId;
		const date = calendarEvent.start;
		if (calendarEvent.meta?.type === CalendarEventTypeEnum.ATTENDING) {
			selectedActivityId = calendarEvent.meta.activityId;
		}

		const dialogRef = this.dialog.open(ActivitySignUpComponent, {
			closeOnNavigation: true,
			panelClass: 'main-form-dialog-container',
			backdropClass: 'custom-backdrop',
			maxWidth: '1030px',
			width: '80%',
			data: {
				flexPeriodId,
				date,
				selectedActivityId,
				calendarEvent,
			},
		});

		return firstValueFrom(dialogRef.afterClosed());
	}

	isCalendarEvent(event: CalendarEvent | CalendarEventResponse): event is CalendarEvent {
		return 'meta' in event;
	}

	isCalendarEventResponse(event: CalendarEvent | CalendarEventResponse): event is CalendarEventResponse {
		return 'school_activity_instance' in event || 'flex_period_id' in event;
	}
}
